import React from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { ROUTES } from '@common'
import { useDashboardRedux, useAuth, useUserRedux } from '@hooks'
import bannerImage from '../../assets/images/light-banner.png'
import './Sidebar.css'

const Sidebar = ({ isOpen, onClose }) => {
    const navigate = useNavigate()
    const location = useLocation()
    const { categories, stalls } = useDashboardRedux()
    const { isAuthenticated, userLogout } = useAuth()
    const { setTargetStallId } = useUserRedux()

    if (!isOpen) return null

    // Check if current page is table page
    const isTablePage = location.pathname === ROUTES.Table.root

    const handleLogout = () => {
        onClose()
        userLogout()
    }

    const handleOrderHistory = () => {
        onClose()
        navigate(ROUTES.Order.history, { replace: false })
    }

    const handleHome = () => {
        onClose()
        navigate(ROUTES.root, { replace: true })
    }

    // const handleMenu = () => {
    //     onClose()
    //     navigate(ROUTES.Table.root, { replace: true })
    // }

    const handleStallClick = stallName => {
        onClose()

        // Find stall ID by name
        const stall = stalls.find(s => s.name === stallName)
        const stallId = stall?.id

        // Always use setTargetStallId for consistent scrolling behavior
        // TablePage will automatically scroll when targetStallId is set
        if (stallId) {
            setTargetStallId(stallId)
            // Only navigate if we're not already on TablePage
            if (location.pathname !== ROUTES.Table.root) {
                navigate(ROUTES.Table.root)
            }
        } else {
            // Fallback: navigate to TablePage and scroll by name
            if (location.pathname !== ROUTES.Table.root) {
                navigate(ROUTES.Table.root)
            }
            setTimeout(() => {
                scrollToStall(stallName, null)
            }, 500)
        }
    }

    const scrollToStall = (stallName, stallId = null) => {
        // Get navbar height for offset
        const navbar = document.querySelector('nav')
        const navbarHeight = navbar ? navbar.getBoundingClientRect().height : 0

        // Extra spacing for scroll offset (matches TablePage)
        const extraSpacing = 30

        // Try to find by ID first (more reliable)
        if (stallId) {
            const sectionById = document.querySelector(`[data-stall-id="${stallId}"]`)
            if (sectionById) {
                const elementPosition = sectionById.getBoundingClientRect().top + window.pageYOffset
                const offsetPosition = elementPosition - navbarHeight - extraSpacing
                window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth',
                })
                return
            }
        }
        // Fallback to name-based search
        const section = document.querySelector(`[data-category="${stallName}"]`)
        if (section) {
            const elementPosition = section.getBoundingClientRect().top + window.pageYOffset
            const offsetPosition = elementPosition - navbarHeight - extraSpacing
            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth',
            })
        }
    }

    return (
        <>
            {/* overlay */}
            <div className="sidebar-overlay" onClick={onClose} />

            {/* Sidebar container */}
            <div className="sidebar-container">
                <div className="sidebar-header">
                    {/* Logo image */}
                    <div className="sidebar-logo">
                        <img src={bannerImage} alt="Local-Chapter-Banner" style={{ maxHeight: '40px', width: '90%' }} />
                    </div>
                    <button className="sidebar-close-btn" onClick={onClose}>
                        <i className="bx bx-x" style={{ fontSize: '28px' }} />
                    </button>
                </div>

                <div className="sidebar-content">
                    <div className="sidebar-menu">
                        <div className="sidebar-menu-item" onClick={handleHome}>
                            <i className="bx bx-home" style={{ fontSize: '24px' }} />
                            <span>Home</span>
                        </div>
                        {/* <div className="sidebar-menu-item" onClick={handleMenu}>
                            <i className="bx bx-menu" style={{ fontSize: '24px' }} />
                            <span>Menu</span>
                        </div> */}
                        {/* Order History - only visible to logged in members */}
                        {isAuthenticated && (
                            <div className="sidebar-menu-item" onClick={handleOrderHistory}>
                                <i className="bx bx-history" style={{ fontSize: '24px' }} />
                                <span>Order History</span>
                            </div>
                        )}

                        {/* Divider and Stalls list - only visible on table page */}
                        {isTablePage && (
                            <>
                                {/* Divider - placed before Stalls */}
                                <div className="sidebar-divider" />

                                {/* Stalls list */}
                                <div className="sidebar-stalls">
                                    {categories && categories.length > 0 ?
                                        categories.map((stall, index) => (
                                            <div
                                                key={index}
                                                className="sidebar-menu-item"
                                                onClick={() => handleStallClick(stall)}
                                            >
                                                <i className="bx bx-store" style={{ fontSize: '24px' }} />
                                                <span>{stall}</span>
                                            </div>
                                        ))
                                    :   <div className="sidebar-menu-item" style={{ color: '#999', cursor: 'default' }}>
                                            <span style={{ fontSize: '14px' }}>No stalls available</span>
                                        </div>
                                    }
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Logout Button - fixed at the bottom */}
                {isAuthenticated && (
                    <div className="sidebar-footer">
                        <button className="sidebar-logout-btn" onClick={handleLogout}>
                            <i className="bx bx-log-out" style={{ fontSize: '24px' }} />
                            <span>Sign out</span>
                        </button>
                    </div>
                )}
            </div>
        </>
    )
}

export default Sidebar
